import React, { useMemo } from 'react';
import _ from 'underscore';

import { CreatorTabContentBackground, CreatorSection, SectionHeader, Label, Text } from '../styles';
import { SubHeader, CodeContainer } from './styles';
import { useFreecodeContextData } from 'hooks/useContext';

export default function FreecodePreview() {
  const { testImpl, languageData } = useFreecodeContextData();

  const renderCodeSnippet = (source) => {
    return (
      <CodeContainer>
        <code>{source}</code>
      </CodeContainer>
    );
  };

  const values = useMemo(() => {
    if (!testImpl?.generated) return {};
    return {
      skeletons: _.map(testImpl.generated.skeletons, (val, key) => ({
        name: languageData?.[key]?.SimpleName || key,
        code: val,
      })),
      apiStrings: {
        challenge: JSON.stringify(testImpl.generated.apiStrings?.challenge, null, 2),
        challengeDescription: JSON.stringify(testImpl.generated.apiStrings?.challengeDescription, null, 2),
        solution: JSON.stringify(testImpl.generated.apiStrings.solution, null, 2),
      },
      autocompletions: JSON.stringify(testImpl.generated.autocompletions, null, 2),
      allLevelNames: JSON.stringify(testImpl.generated.allLevelNames, null, 2),
    };
  }, [testImpl, languageData]);

  return (
    <CreatorTabContentBackground>
      <CreatorSection paddingX>
        <Label>The contents of this tab have been automatically generated and can not be manually edited.</Label>
      </CreatorSection>
      {values.skeletons && (
        <CreatorSection paddingX>
          <SectionHeader>Skeletons</SectionHeader>
          <Text>These are the boilerplate code snippets a user starts with when creating a solution.</Text>
          {values.skeletons.map((element, index) => (
            <React.Fragment key={index}>
              <SubHeader>{element.name}</SubHeader>
              {renderCodeSnippet(element.code)}
            </React.Fragment>
          ))}
        </CreatorSection>
      )}
      {values.apiStrings && (
        <CreatorSection paddingX>
          <SectionHeader>API Strings</SectionHeader>
          <Text>Function display names in different languages for use in the API description.</Text>
          <SubHeader>Challenge API names</SubHeader>
          {renderCodeSnippet(values.apiStrings.challenge, 'json')}
          <SubHeader>Challenge API generated description</SubHeader>
          {renderCodeSnippet(values.apiStrings.challengeDescription, 'json')}
          <SubHeader>Solution API names</SubHeader>
          {renderCodeSnippet(values.apiStrings.solution, 'json')}
        </CreatorSection>
      )}
      {values.autocompletions && (
        <CreatorSection paddingX>
          <SectionHeader>Generated auto completion values</SectionHeader>
          <Text>Additional auto completion values sent by the executor.</Text>
          {renderCodeSnippet(values.autocompletions, 'json')}
        </CreatorSection>
      )}
      {values.allLevelNames && (
        <CreatorSection paddingX>
          <SectionHeader>Generated level names</SectionHeader>
          <Text>The level names returned from the executor.</Text>
          {renderCodeSnippet(values.allLevelNames, 'json')}
        </CreatorSection>
      )}
    </CreatorTabContentBackground>
  );
}
